import { useState, useEffect } from "react";
import { Search, Plus, Pencil, Trash2, Phone, Users, AlertCircle, FileText, MoreHorizontal } from "lucide-react";
import { type Note, type NoteType, listNotes, deleteNote, createNote, updateNote } from "../../api/notes";
import { IconButton } from "../primitives/IconButton";
import { cn } from "../utils/cn";
import { NoteFormModal } from "./NoteFormModal";

type NotesListProps = {
  entityType: string;
  entityId: string;
  onNoteCreated?: () => void;
  onNoteUpdated?: () => void;
  onNoteDeleted?: () => void;
};

const NOTE_TYPES: { value: NoteType; label: string; icon: React.ReactNode }[] = [
  { value: "general", label: "Notes", icon: <FileText size={14} strokeWidth={1.5} /> },
  { value: "call", label: "Appel", icon: <Phone size={14} strokeWidth={1.5} /> },
  { value: "meeting", label: "Rencontre", icon: <Users size={14} strokeWidth={1.5} /> },
  { value: "complaint", label: "Plainte", icon: <AlertCircle size={14} strokeWidth={1.5} /> },
  { value: "other", label: "Autre", icon: <MoreHorizontal size={14} strokeWidth={1.5} /> },
];

export function NotesList({ entityType, entityId, onNoteCreated, onNoteUpdated, onNoteDeleted }: NotesListProps) {
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedType, setSelectedType] = useState<NoteType | "all">("all");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editNote, setEditNote] = useState<Note | null>(null);
  const [initialNoteType, setInitialNoteType] = useState<NoteType>("general");

  const fetchNotes = async () => {
    setLoading(true);
    try {
      const data = await listNotes({
        entity_type: entityType,
        entity_id: entityId,
        note_type: selectedType !== "all" ? selectedType : undefined,
        search: searchQuery || undefined,
        page_size: 100,
      });
      setNotes(data.items);
    } catch (error) {
      console.error("Failed to load notes:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotes();
  }, [entityType, entityId, selectedType, searchQuery]);

  const handleCreate = async (data: any) => {
    await createNote(data);
    await fetchNotes();
    onNoteCreated?.();
  };

  const handleUpdate = async (data: any) => {
    if (!editNote) return;
    await updateNote(editNote.id, data);
    await fetchNotes();
    setEditNote(null);
    onNoteUpdated?.();
  };

  const handleDelete = async (noteId: string) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer cette note ?")) return;
    try {
      await deleteNote(noteId);
      await fetchNotes();
      onNoteDeleted?.();
    } catch (error) {
      console.error("Failed to delete note:", error);
      alert("Erreur lors de la suppression de la note");
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("fr-CA", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const getUserDisplayName = (note: Note) => {
    if (note.created_by_user?.display_name) return note.created_by_user.display_name;
    if (note.created_by_user?.first_name || note.created_by_user?.last_name) {
      return `${note.created_by_user.first_name || ""} ${note.created_by_user.last_name || ""}`.trim();
    }
    return note.created_by_user?.email || "Utilisateur inconnu";
  };

  const getNoteTypeInfo = (type: NoteType) => {
    return NOTE_TYPES.find((t) => t.value === type) || NOTE_TYPES[0];
  };

  return (
    <div className="space-y-4">
      {/* Header with search and create button */}
      <div className="flex items-center gap-3">
        <IconButton
          icon={<Plus size={16} strokeWidth={1.5} />}
          ariaLabel="Ajouter une note"
          intent="primary"
          size="sm"
          onClick={() => {
            setInitialNoteType("general");
            setCreateModalOpen(true);
          }}
        />
        <div className="flex-1 relative">
          <Search size={16} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
          <input
            type="text"
            placeholder="Rechercher dans les notes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-md border border-border bg-surface text-sm text-text placeholder:text-muted"
          />
        </div>
      </div>

      {/* Type filter tabs */}
      <div className="flex gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => setSelectedType("all")}
          className={cn(
            "px-3 py-1.5 rounded-md text-xs font-medium transition",
            selectedType === "all"
              ? "bg-primary text-primary-foreground"
              : "bg-surface-muted text-text hover:bg-surface"
          )}
        >
          Tous
        </button>
        {NOTE_TYPES.map((type) => (
          <button
            key={type.value}
            type="button"
            onClick={() => setSelectedType(type.value)}
            className={cn(
              "px-3 py-1.5 rounded-md text-xs font-medium transition flex items-center gap-1.5",
              selectedType === type.value
                ? "bg-primary text-primary-foreground"
                : "bg-surface-muted text-text hover:bg-surface"
            )}
          >
            {type.icon}
            {type.label}
          </button>
        ))}
      </div>

      {/* Notes list */}
      {loading ? (
        <div className="text-sm text-muted text-center py-8">Chargement...</div>
      ) : notes.length === 0 ? (
        <div className="text-sm text-muted text-center py-8">
          {searchQuery || selectedType !== "all"
            ? "Aucune note ne correspond aux filtres"
            : "Aucune note pour le moment"}
        </div>
      ) : (
        <div className="space-y-3">
          {notes.map((note) => {
            const typeInfo = getNoteTypeInfo(note.note_type);
            return (
              <div
                key={note.id}
                className="rounded-md border border-border bg-surface p-4 shadow-sm hover:shadow transition"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-muted">{typeInfo.icon}</span>
                      <span className="text-xs font-medium text-text">{typeInfo.label}</span>
                      <span className="text-xs text-muted">•</span>
                      <span className="text-xs text-muted">
                        Créé le {formatDate(note.created_at)}
                      </span>
                      {note.updated_at && new Date(note.updated_at).getTime() !== new Date(note.created_at).getTime() && (
                        <>
                          <span className="text-xs text-muted">•</span>
                          <span className="text-xs text-muted">
                            Modifié le {formatDate(note.updated_at)}
                          </span>
                        </>
                      )}
                      <span className="text-xs text-muted">•</span>
                      <span className="text-xs text-muted">{getUserDisplayName(note)}</span>
                    </div>
                    <div className="text-sm text-text whitespace-pre-wrap">{note.content}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <IconButton
                      icon={<Pencil size={14} strokeWidth={1.5} />}
                      ariaLabel="Modifier"
                      intent="ghost"
                      size="sm"
                      onClick={() => setEditNote(note)}
                    />
                    <IconButton
                      icon={<Trash2 size={14} strokeWidth={1.5} />}
                      ariaLabel="Supprimer"
                      intent="ghost"
                      size="sm"
                      onClick={() => handleDelete(note.id)}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit modals */}
      <NoteFormModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSubmit={handleCreate}
        entityType={entityType}
        entityId={entityId}
        initialNoteType={initialNoteType}
      />
      {editNote && (
        <NoteFormModal
          open={!!editNote}
          onClose={() => setEditNote(null)}
          onSubmit={handleUpdate}
          entityType={entityType}
          entityId={entityId}
          note={editNote}
        />
      )}
    </div>
  );
}
